import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  EmployeeAvatar,
  Icon,
  IconBtn,
  Input,
  PageHeader,
  Spinner,
  Stat,
  Tabs,
} from "../../design-system";
import EmpleadoDetalleDrawer from "../../Components/Kronaly/Empleados/EmpleadoDetalleDrawer";
import NewEmpleadoModal from "../../Components/Kronaly/Empleados/NewEmpleadoModal";
import {
  getEmployees,
  getEmployeeActiveContract,
  getVacationPolicies,
  getWorkSchedules,
  getWorkspaceTodayStatus,
} from "../../helpers/kronaly_helper";
import { mapApiEmployeeToUi, todayDowKey } from "../../helpers/employees_mapper";

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const STATUS_BY_TAB = {
  activos: "active",
  baja: "terminated",
};

function Empleados() {
  document.title = "Empleados | Kronaly";
  const [tab, setTab] = useState("activos");
  const [team, setTeam] = useState("all");
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [search, setSearch] = useState("");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRows = useCallback(async (statusKey) => {
    setLoading(true);
    setError(null);
    try {
      const params = STATUS_BY_TAB[statusKey]
        ? { status: STATUS_BY_TAB[statusKey] }
        : undefined;
      const [empRes, schedRes, todayRes, polRes] = await Promise.all([
        getEmployees(params),
        getWorkSchedules().catch(() => ({ data: [] })),
        getWorkspaceTodayStatus().catch(() => ({ data: {} })),
        getVacationPolicies().catch(() => ({ data: [] })),
      ]);
      const list = Array.isArray(empRes?.data)
        ? empRes.data
        : Array.isArray(empRes)
          ? empRes
          : [];
      const schedules = unwrap(schedRes) || [];
      const schedulesById = {};
      (Array.isArray(schedules) ? schedules : []).forEach((s) => {
        if (s?.id) schedulesById[s.id] = s;
      });
      const todayStatusByEmployee = unwrap(todayRes) || {};

      const policies = unwrap(polRes) || [];
      const policiesById = {};
      (Array.isArray(policies) ? policies : []).forEach((p) => {
        if (p?.id) policiesById[p.id] = p;
      });

      const contractsByEmployee = {};
      await Promise.all(
        list.map(async (e) => {
          if (!e?.id) return;
          try {
            const r = await getEmployeeActiveContract(e.id);
            const data = unwrap(r);
            if (data && (data.contract || data.id)) {
              contractsByEmployee[e.id] = data;
            }
          } catch (_) {
            // ignore individual errors
          }
        }),
      );

      const todayDow = todayDowKey();
      setRows(
        list.map((e) =>
          mapApiEmployeeToUi(e, {
            activeContract: contractsByEmployee[e.id] || null,
            schedulesById,
            policiesById,
            todayStatus: todayStatusByEmployee[e.id] || null,
            todayDow,
          }),
        ),
      );
    } catch (e) {
      setRows([]);
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar los empleados.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "ausentes" || tab === "alta") {
      setRows([]);
      setLoading(false);
      setError(null);
      return;
    }
    fetchRows(tab);
  }, [tab, fetchRows]);

  const teams = useMemo(
    () => ["all", ...Array.from(new Set(rows.map((e) => e.team).filter((t) => t && t !== "—")))],
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((e) => {
      if (team !== "all" && e.team !== team) return false;
      if (!q) return true;
      return (
        (e.name || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q) ||
        (e.role || "").toLowerCase().includes(q) ||
        (e.team || "").toLowerCase().includes(q)
      );
    });
  }, [rows, team, search]);

  const counts = useMemo(
    () => ({
      activos: rows.filter((r) => r.status === "active").length,
      baja: rows.filter((r) => r.status === "terminated").length,
    }),
    [rows]
  );

  const onCreated = () => {
    fetchRows(tab);
  };

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow={`${rows.length} ${rows.length === 1 ? "empleado" : "empleados"}`}
        title="Empleados"
        description="Gestiona contratos, horarios y permisos del equipo."
        actions={
          <>
            <Button variant="secondary" icon="Upload">
              Importar CSV
            </Button>
            <Button variant="primary" icon="Plus" onClick={() => setShowNew(true)}>
              Añadir empleado
            </Button>
          </>
        }
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 14,
        }}
      >
        <Stat label="Plantilla" value={rows.length} />
        <Stat label="Activos" value={counts.activos} />
        <Stat label="Bajas" value={counts.baja} />
        <Stat label="Trabajando ahora" value="—" hint="Próximamente" />
      </div>

      <Card padded={false}>
        <div style={{ padding: "12px 16px 0" }}>
          <Tabs
            tabs={[
              { id: "activos", label: "Activos", count: counts.activos },
              { id: "ausentes", label: "Ausentes hoy" },
              { id: "alta", label: "Alta reciente" },
              { id: "baja", label: "Bajas", count: counts.baja },
            ]}
            value={tab}
            onChange={setTab}
          />
        </div>

        <FilterBar
          search={search}
          setSearch={setSearch}
          team={team}
          setTeam={setTeam}
          teams={teams}
        />

        {error && (
          <div style={{ padding: "12px 20px" }}>
            <Alert tone="danger">{error}</Alert>
          </div>
        )}

        {loading ? (
          <LoadingRow />
        ) : tab === "ausentes" || tab === "alta" ? (
          <EmptyState
            icon={tab === "ausentes" ? "Calendar" : "UserPlus"}
            title={tab === "ausentes" ? "Ausentes hoy" : "Alta reciente"}
            description="Disponible cuando integremos jornada y altas en este listado."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={search.trim() ? "Search" : "Users"}
            title={
              search.trim()
                ? "Sin resultados"
                : tab === "baja"
                  ? "No hay empleados dados de baja"
                  : "Aún no hay empleados"
            }
            description={
              search.trim()
                ? "Prueba con otro nombre, email o equipo."
                : tab === "baja"
                  ? "Cuando des de baja a alguien aparecerá aquí."
                  : "Añade el primer empleado para empezar."
            }
            action={
              !search.trim() && tab !== "baja"
                ? {
                    label: "Añadir empleado",
                    icon: "Plus",
                    onClick: () => setShowNew(true),
                  }
                : undefined
            }
          />
        ) : (
          <EmpleadosTable rows={filtered} onOpen={setSelected} />
        )}

        <TableFooter shown={filtered.length} total={rows.length} />
      </Card>

      <EmpleadoDetalleDrawer
        empleado={selected}
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
      />

      <NewEmpleadoModal
        open={showNew}
        onClose={() => setShowNew(false)}
        onCreated={onCreated}
      />
    </div>
  );
}

function FilterBar({ search, setSearch, team, setTeam, teams }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "12px 16px",
        borderBottom: "1px solid var(--kr-border-subtle)",
        flexWrap: "wrap",
      }}
    >
      <Input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Buscar por nombre, email o equipo…"
        icon="Search"
        style={{ flex: 1, maxWidth: 360 }}
      />
      {teams.length > 1 && (
        <>
          <span
            style={{
              fontSize: 11,
              color: "var(--kr-fg-muted)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Equipo
          </span>
          {teams.map((t) => (
            <Chip key={t} active={team === t} onClick={() => setTeam(t)}>
              {t === "all" ? "Todos" : t}
            </Chip>
          ))}
        </>
      )}
      <div style={{ flex: 1 }} />
      <Button variant="ghost" size="sm" icon="Filter">
        Más filtros
      </Button>
    </div>
  );
}

function Chip({ active, onClick, icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 28,
        padding: "0 10px",
        fontSize: 12,
        fontWeight: 500,
        border: `1px solid ${active ? "var(--kr-action)" : "var(--kr-border)"}`,
        background: active ? "var(--kr-action-soft-bg)" : "#fff",
        color: active ? "var(--kr-action-soft-fg)" : "var(--kr-fg-strong)",
        borderRadius: 6,
        cursor: "pointer",
      }}
    >
      {icon && <Icon name={icon} size={12} />}
      {children}
    </button>
  );
}

function LoadingRow() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        padding: "48px 24px",
        color: "var(--kr-fg-muted)",
        fontSize: 13,
      }}
    >
      <Spinner size={16} />
      <span>Cargando empleados…</span>
    </div>
  );
}

function TableFooter({ shown, total }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 20px",
        borderTop: "1px solid var(--kr-border-subtle)",
        color: "var(--kr-fg-muted)",
        fontSize: 12,
      }}
    >
      <span>
        <span style={{ color: "var(--kr-fg-strong)", fontWeight: 600 }}>
          {shown}
        </span>{" "}
        de {total} empleados
      </span>
    </div>
  );
}

function EmpleadosTable({ rows, onOpen }) {
  const cols = "2fr 1.2fr 1.4fr 1fr 1.2fr 1fr 0.8fr";
  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: cols,
          gap: 12,
          padding: "10px 20px",
          background: "var(--kr-neutral-50)",
          borderBottom: "1px solid var(--kr-border)",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 600,
          color: "var(--kr-fg-muted)",
        }}
      >
        <span>Empleado</span>
        <span>Equipo</span>
        <span>Contrato</span>
        <span>Horario</span>
        <span>Estado hoy</span>
        <span>Vacaciones</span>
        <span style={{ textAlign: "right" }}></span>
      </div>
      {rows.map((r, i) => (
        <EmpleadoRow
          key={r.id || r.name + i}
          row={r}
          cols={cols}
          onOpen={() => onOpen(r)}
          last={i === rows.length - 1}
        />
      ))}
    </div>
  );
}

function EmpleadoRow({ row, cols, onOpen, last }) {
  const [hover, setHover] = useState(false);
  const timeSuffix = row.time ? ` · ${row.time}` : "";
  const stateBadge = {
    working: (
      <Badge tone="success" dot>
        Trabajando{timeSuffix}
      </Badge>
    ),
    paused: (
      <Badge tone="warning" dot>
        En pausa{timeSuffix}
      </Badge>
    ),
    finished: (
      <Badge tone="info" dot>
        Finalizada{timeSuffix}
      </Badge>
    ),
    missed: (
      <Badge tone="danger" dot>
        No trabajado
      </Badge>
    ),
    "non-working": (
      <Badge tone="neutral" dot>
        No laborable
      </Badge>
    ),
    off: (
      <Badge tone="neutral" dot>
        —
      </Badge>
    ),
  }[row.state];
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onOpen}
      style={{
        display: "grid",
        gridTemplateColumns: cols,
        gap: 12,
        alignItems: "center",
        padding: "12px 20px",
        borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        background: hover ? "var(--kr-bg-subtle)" : "#fff",
        cursor: "pointer",
        fontSize: 13,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          minWidth: 0,
        }}
      >
        <EmployeeAvatar
          employeeId={row.id}
          hasAvatar={!!row.avatarPath}
          name={row.name}
          size={32}
          status={
            row.state === "working"
              ? "working"
              : row.state === "paused"
                ? "paused"
                : null
          }
        />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: "var(--kr-fg-strong)",
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.name}
          </div>
          <div
            style={{
              color: "var(--kr-fg-muted)",
              fontSize: 11,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {row.role}
            {row.email ? ` · ${row.email}` : ""}
          </div>
        </div>
      </div>
      <div style={{ color: "var(--kr-fg-strong)" }}>{row.team}</div>
      <div>
        <div
          style={{
            color: row.hasContract ? "var(--kr-fg-strong)" : "var(--kr-fg-muted)",
          }}
        >
          {row.contract}
        </div>
        {row.hasContract && row.since !== "—" && (
          <div style={{ color: "var(--kr-fg-muted)", fontSize: 11 }}>
            desde {row.since}
          </div>
        )}
      </div>
      <div
        style={{
          fontFamily: "var(--kr-font-mono)",
          fontVariantNumeric: "tabular-nums",
          color: "var(--kr-fg-strong)",
        }}
      >
        {row.schedule}
      </div>
      <div>{stateBadge}</div>
      <div
        style={{
          fontFamily: "var(--kr-font-mono)",
          fontVariantNumeric: "tabular-nums",
          color: "var(--kr-fg-strong)",
          fontWeight: 600,
        }}
      >
        {row.balance}
        {row.balanceProrated && (
          <div style={{ fontSize: 10, color: "var(--kr-fg-muted)", fontWeight: 400 }}>
            de {row.balanceAnnual} · prorrateado
          </div>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 4 }}>
        <IconBtn icon="MoreHorizontal" />
      </div>
    </div>
  );
}

export default Empleados;
